import React, { useState, useEffect } from 'react';

interface Props {
  /** Ref to the scroll container div (the one with overflow-y-scroll) */
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}

/**
 * Slim fixed bottom bar that slides up once the user scrolls past the
 * hero section (>80% of viewport height). Dismissible for the session.
 * Only renders if at least one store URL env var is set.
 */
const GetAppBar: React.FC<Props> = ({ scrollContainerRef }) => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const appleUrl = import.meta.env.VITE_APPLE_STORE_URL as string | undefined;
  const playUrl = import.meta.env.VITE_PLAY_STORE_URL as string | undefined;

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const threshold = window.innerHeight * 0.8;

    const handleScroll = () => {
      setVisible(el.scrollTop > threshold);
      // Un-dismiss if user scrolls back to the top
      if (el.scrollTop <= threshold) setDismissed(false);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [scrollContainerRef]);

  if (!appleUrl && !playUrl) return null;
  if (!visible || dismissed) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 animate-slide-up"
      style={{ animation: 'slideUp 0.3s ease-out' }}
    >
      <div className="bg-black/75 backdrop-blur-md border-t border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-2.5 flex items-center justify-between gap-4">
          {/* Label */}
          <span className="text-white/60 text-xs font-medium tracking-wide whitespace-nowrap hidden sm:block">
            Explore more with Blip
          </span>

          {/* Store badges */}
          <div className="flex items-center gap-3 flex-1 justify-center sm:justify-start">
            {appleUrl && (
              <a
                href={appleUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download on the App Store"
                className="opacity-80 hover:opacity-100 transition-opacity"
              >
                <img src="/images/badges/app-store-badge.svg" alt="App Store" className="h-8" />
              </a>
            )}
            {playUrl && (
              <a
                href={playUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get it on Google Play"
                className="opacity-80 hover:opacity-100 transition-opacity"
              >
                <img src="/images/badges/google-play-badge.svg" alt="Google Play" className="h-8" />
              </a>
            )}
          </div>

          {/* Social icons */}
          <div className="flex items-center gap-3">
            <a
              href="https://www.instagram.com/blip.travel/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-white/35 hover:text-white/80 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a
              href="https://www.youtube.com/@BlipApp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              className="text-white/35 hover:text-white/80 transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
          </div>

          {/* Dismiss */}
          <button
            onClick={() => setDismissed(true)}
            className="text-white/30 hover:text-white/70 transition-colors p-1 flex-shrink-0"
            aria-label="Dismiss"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default GetAppBar;
